import { Button, ButtonPrimary } from '@/components/button'
import { ArrowBack } from '@/components/icons'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
/* eslint-disable react-hooks/exhaustive-deps */
import { MainContent } from '@/components/content'
import { AutocompleteAddress, AutocompleteLocationInput, LogoInput, TextArea, TextInput } from '@/components/input'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_EVENTS } from '@/constants/Routes'
import { userSelector } from '@/pages/user/selectors'
import { uploadImage, useEditEvent, useGetEvent, useSaveAddress } from '@/services'
import editEventValidationSchema from '@/utils/schema/EditEventValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

export type EditEventForm = {
  logo: File | null
  logoEdit?: { url: string; id: number }
  registrationUrl?: string
  leagueName: string
  address: AutocompleteAddress
  longDescription: string
  longDescriptionContentId?: number
}

export const EditEvent = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const { eventId } = params

  const { data: eventData } = useGetEvent(eventId)
  const { mutate: saveAddress } = useSaveAddress()
  const { mutate: editEvent } = useEditEvent()

  const defaultValues: EditEventForm = {
    logo: null,
    leagueName: eventData?.result.leagueName || '',
    address: {
      description: eventData?.result.address?.formattedAddress
        ? eventData?.result.address?.formattedAddress
        : eventData?.result.address?.shortAddress || '',
      placeId: eventData?.result.address?.id.toString() || '',
    },
    registrationUrl: eventData?.result.registrationUrl,
    longDescription: eventData?.result?.longDescription?.content || '',
  }

  const {
    setValue,
    control,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditEventForm>({
    defaultValues,
    resolver: yupResolver(editEventValidationSchema as any),
    mode: 'onTouched',
  })

  const onGoBack = () => navigate(-1)

  const onSubmit = handleSubmit(async (data) => {
    let responseImg = null

    if (eventData && user) {
      if (data.logo) {
        responseImg = await uploadImage(data.logo)
      }

      const payload = {
        ...eventData.result,
        leagueName: data.leagueName,
        registrationUrl: data.registrationUrl,
        longDescription: {
          content: data.longDescription,
          contentType: 'text/html',
          ...(data.longDescriptionContentId && { contentId: data.longDescriptionContentId }),
        },

        userId: user.id,

        ...(responseImg && { mediaId: responseImg.result.id }),
      }

      if (data?.address?.placeId !== eventData?.result.address.id.toString()) {
        saveAddress(
          { placeId: data.address.placeId },
          {
            onSuccess: (data) => {
              const reqData = {
                ...payload,
                addressId: data.id,
              }
              editEvent(reqData, {
                onSuccess: () => {
                  navigate(`${ROUTE_EVENTS}/${eventId}`)
                },
                onError: (error: any) => {
                  toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
                },
              })
            },
          }
        )
      }

      if (data?.address?.placeId === eventData?.result.address.id.toString()) {
        editEvent(payload, {
          onSuccess: () => {
            navigate(`${ROUTE_EVENTS}/${eventId}`)
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
          },
        })
      }
    }
  })

  useEffect(() => {
    if (eventData) {
      setValue('logoEdit', { url: eventData.result.mediaUrl, id: eventData.result.mediaId })
      setValue('leagueName', eventData.result?.leagueName, {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('address', {
        description: eventData.result.address?.formattedAddress
          ? eventData.result.address?.formattedAddress
          : eventData.result.address?.shortAddress || '',
        placeId: eventData.result.address?.id.toString() || '',
      })
      setValue('longDescription', eventData.result?.longDescription?.content)
      setValue('registrationUrl', eventData.result?.registrationUrl)
      setValue('longDescriptionContentId', eventData.result?.longDescription?.contentId)
      reset({
        logoEdit: { url: eventData?.result?.mediaUrl, id: eventData?.result?.mediaId },
        leagueName: eventData?.result.leagueName || '',
        registrationUrl: eventData?.result?.registrationUrl,
        address: {
          description: eventData?.result.address?.formattedAddress
            ? eventData?.result.address?.formattedAddress
            : eventData?.result.address?.shortAddress || '',
          placeId: eventData?.result.address?.id.toString() || '',
        },
        longDescription: eventData?.result?.longDescription?.content || '',
      })
    }
  }, [eventData])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col mb-20 lg:mb-8">
            <div className="flex items-center justify-between">
              <div className="flex gap-x-3 items-center">
                <Button onClick={onGoBack} className="hover:opacity-50">
                  <ArrowBack className="text-white" />
                </Button>
                <h1 className="text-sm md:text-xl text-white font-semibold">{t('event.editEvent')}</h1>
              </div>
              <div className="flex gap-x-4">
                <ButtonPrimary title={t('common.saveChanges')} className="py-3 px-5" onClick={onSubmit} />
              </div>
            </div>

            <div className="flex flex-col gap-y-4">
              <div className="pt-4 pb-2">
                <h3 className="text-xl font-semibold text-white">{t('club.eventDetails')}</h3>
              </div>

              <LogoInput
                onSave={(logo: File | null) => setValue('logo', logo, { shouldValidate: true })}
                label={t('common.logo')}
                defaultSrc={watch('logoEdit.url')}
                error={errors.logo?.message}
                onRemoveProfileLogo={() =>
                  setValue('logo', null, {
                    shouldValidate: true,
                  })
                }
                uploadImageModalTitle={t('common.eventLogo')}
              />

              <div className="flex gap-x-10">
                <TextInput
                  label={t('common.eventName')}
                  className="w-full"
                  placeholder={t('common.eventName')}
                  {...register('leagueName')}
                  error={errors?.leagueName?.message}
                />
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <AutocompleteLocationInput
                      {...field}
                      label={t('common.location')}
                      containerClassName="w-full"
                      setValue={(location) =>
                        location &&
                        setValue('address', location, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      error={errors?.address?.description?.message}
                    />
                  )}
                />
              </div>
              <TextInput
                label={t('common.registration_url')}
                className="w-full"
                placeholder={t('common.registration_url')}
                {...register('registrationUrl')}
                error={errors?.registrationUrl?.message}
              />

              <Controller
                name="longDescription"
                control={control}
                render={({ field, formState }) => (
                  <TextArea
                    label={t('common.summary')}
                    placeholder={t('common.whatAreTheDetails')}
                    error={errors?.longDescription?.message}
                    defaultValue={formState.defaultValues?.longDescription}
                    {...field}
                  />
                )}
              />
            </div>
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
